import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTierLimits } from '../hooks/useTierLimits';
import { useTheme } from '../contexts/ThemeContext';
import Header from '../components/Header';
import QuotaDisplay from '../components/QuotaDisplay';
import './Settings.css';

// Helper functions for color conversion
const rgbaToHex = (rgba) => {
  if (!rgba || rgba === 'rgba(0, 0, 0, 0)') return '#000000';
  const matches = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!matches) return '#000000';
  const r = parseInt(matches[1]).toString(16).padStart(2, '0');
  const g = parseInt(matches[2]).toString(16).padStart(2, '0');
  const b = parseInt(matches[3]).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getAlpha = (rgba) => {
  if (!rgba || rgba === 'rgba(0, 0, 0, 0)') return 0;
  const matches = rgba.match(/rgba?\([^)]+,\s*([0-9.]+)\)/);
  return matches ? parseFloat(matches[1]) : 0.3;
};

const setAlpha = (rgba, alpha) => {
  if (!rgba || rgba === 'rgba(0, 0, 0, 0)') return `rgba(0, 0, 0, ${alpha})`;
  const matches = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!matches) return `rgba(0, 0, 0, ${alpha})`;
  return `rgba(${matches[1]}, ${matches[2]}, ${matches[3]}, ${alpha})`;
};

// Default highlight colors
const DEFAULT_COLORS = {
  highConfidence: 'rgba(144, 238, 144, 0)',
  mediumConfidence: 'rgba(255, 255, 153, 0.3)',
  lowConfidence: 'rgba(255, 182, 193, 0.3)'
};

function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile settings
  const [email, setEmail] = useState('');

  // App settings
  const [showConfidence, setShowConfidence] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [hideGhosted, setHideGhosted] = useState(false);
  const [highThreshold, setHighThreshold] = useState(0.8);
  const [mediumThreshold, setMediumThreshold] = useState(0.5);
  const [typingMode, setTypingMode] = useState(false);
  const [showSegments, setShowSegments] = useState(true);
  const [segmentDuration, setSegmentDuration] = useState(10); // Default 10 seconds
  const [clickToDeleteMode, setClickToDeleteMode] = useState(false);
  const [skipDuration, setSkipDuration] = useState(5);
  const [shareLinkExpirationDays, setShareLinkExpirationDays] = useState(30);
  const [useAiGeneratedNames, setUseAiGeneratedNames] = useState(false);
  const [showClickToDeleteToggle, setShowClickToDeleteToggle] = useState(false);
  const [showTypingModeToggle, setShowTypingModeToggle] = useState(true); // Default to true
  const [showDeleteModeToggle, setShowDeleteModeToggle] = useState(false); // Default to false
  const [showSuggestionsModeToggle, setShowSuggestionsModeToggle] = useState(true); // Default to true
  const [confirmTranscriptDelete, setConfirmTranscriptDelete] = useState(true);
  const [showConfidenceLegend, setShowConfidenceLegend] = useState(true); // Default to true for bin navigation
  const [enableBinNavigation, setEnableBinNavigation] = useState(false); // Default to false
  const [maxSilenceInterval, setMaxSilenceInterval] = useState(1.0); // Default 1.0 seconds for live transcription
  const [enableDiarization, setEnableDiarization] = useState(false); // Default to false
  // Add this ONE line with the other settings:
  const [enableFolderUpload, setEnableFolderUpload] = useState(false);
  // Highlight color settings
  const [useCustomColors, setUseCustomColors] = useState(false);
  const [highConfidenceColor, setHighConfidenceColor] = useState(DEFAULT_COLORS.highConfidence);
  const [mediumConfidenceColor, setMediumConfidenceColor] = useState(DEFAULT_COLORS.mediumConfidence);
  const [lowConfidenceColor, setLowConfidenceColor] = useState(DEFAULT_COLORS.lowConfidence);

  // Confirmation modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // API Keys and Obsidian integration
  const [apiKeys, setApiKeys] = useState([]);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({}); // Track which keys are visible
  const [revealedKeys, setRevealedKeys] = useState({}); // Store decrypted keys
  const [loadingKeys, setLoadingKeys] = useState({}); // Track loading state for reveal
  const [obsidianPreferences, setObsidianPreferences] = useState({
    recordingsFolder: 'Rec-Chord',
    transcriptsFolder: 'Rec-Chord',
    syncAudio: true,
    lastModified: null
  });
  const [obsidianSyncStatus, setObsidianSyncStatus] = useState(null);

  const { user, isAuthenticated, isLoading: authLoading, logout, updateUser, refreshUserData } = useAuth();
  const { currentTier, limits, getNextTierName, canUpgrade } = useTierLimits();
  const { theme, setTheme, resolvedTheme, isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect while still loading auth state
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    if (user) {
      setEmail(user.email || '');
      // Load user preferences from localStorage or user settings
      loadUserPreferences();
    }
  }, [isAuthenticated, user, navigate, authLoading]);

  const loadUserPreferences = async () => {
    const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    setShowConfidence(preferences.showConfidence === true);
    setNotifications(preferences.notifications !== false);
    setHideGhosted(preferences.hideGhosted === true);
    setHighThreshold(preferences.highThreshold || 0.8);
    setMediumThreshold(preferences.mediumThreshold || 0.5);
    setTypingMode(preferences.typingMode === true);
    setShowSegments(preferences.showSegments !== false); // Default to true
    setSegmentDuration(preferences.segmentDuration || 10); // Default 10 seconds
    setClickToDeleteMode(preferences.clickToDeleteMode === true);
    setSkipDuration(preferences.skipDuration || 5);
    setShareLinkExpirationDays(preferences.shareLinkExpirationDays || 30);
    setUseAiGeneratedNames(preferences.useAiGeneratedNames === true);
    setShowClickToDeleteToggle(preferences.showClickToDeleteToggle === true); // Default to false
    setShowTypingModeToggle(preferences.showTypingModeToggle !== false); // Default to true
    setShowDeleteModeToggle(preferences.showDeleteModeToggle === true); // Default to false
    setShowSuggestionsModeToggle(preferences.showSuggestionsModeToggle !== false); // Default to true
    setConfirmTranscriptDelete(preferences.confirmTranscriptDelete !== false); // Default to true
    setShowConfidenceLegend(preferences.showConfidenceLegend !== false); // Default to true
    setEnableBinNavigation(preferences.enableBinNavigation === true); // Default to false
    setMaxSilenceInterval(preferences.maxSilenceInterval || 1.0); // Default 1.0 seconds
    setEnableDiarization(preferences.enableDiarization === true); // Default to false
    setEnableFolderUpload(preferences.enableFolderUpload === true);

    // Load highlight colors
    setUseCustomColors(preferences.useCustomColors === true);
    setHighConfidenceColor(preferences.highConfidenceColor || DEFAULT_COLORS.highConfidence);
    setMediumConfidenceColor(preferences.mediumConfidenceColor || DEFAULT_COLORS.mediumConfidence);
    setLowConfidenceColor(preferences.lowConfidenceColor || DEFAULT_COLORS.lowConfidence);

    // Load Obsidian preferences from server
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/settings/obsidian-preferences', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const obsidianPrefs = await response.json();
        setObsidianPreferences(obsidianPrefs);
      }
    } catch (error) {
      console.error('Error loading Obsidian preferences:', error);
    }
  };

  // Auto-save preferences whenever they change
  useEffect(() => {
    // Skip on initial mount (when loading from localStorage)
    if (!user) return;

    const preferences = {
      showConfidence,
      notifications,
      hideGhosted,
      highThreshold,
      mediumThreshold,
      typingMode,
      showSegments,
      segmentDuration,
      clickToDeleteMode,
      skipDuration,
      shareLinkExpirationDays,
      useAiGeneratedNames,
      showClickToDeleteToggle,
      showTypingModeToggle,
      showDeleteModeToggle,
      showSuggestionsModeToggle,
      confirmTranscriptDelete,
      showConfidenceLegend,
      enableBinNavigation,
      maxSilenceInterval,
      enableDiarization,
      useCustomColors,
      highConfidenceColor,
      mediumConfidenceColor,
      lowConfidenceColor,
      enableFolderUpload,

    };

    localStorage.setItem('userPreferences', JSON.stringify(preferences));

    // Dispatch custom event to notify other components (like App.js) that preferences changed
    window.dispatchEvent(new Event('preferencesChanged'));
  }, [
    showConfidence,
    notifications,
    hideGhosted,
    highThreshold,
    mediumThreshold,
    typingMode,
    showSegments,
    segmentDuration,
    clickToDeleteMode,
    skipDuration,
    shareLinkExpirationDays,
    useAiGeneratedNames,
    showClickToDeleteToggle,
    showTypingModeToggle,
    showDeleteModeToggle,
    showSuggestionsModeToggle,
    confirmTranscriptDelete,
    showConfidenceLegend,
    enableBinNavigation,
    maxSilenceInterval,
    enableDiarization,
    useCustomColors,
    highConfidenceColor,
    mediumConfidenceColor,
    lowConfidenceColor,
    enableFolderUpload,

    user
  ]);  

  const handleHighThresholdChange = (value) => {
    const newValue = parseFloat(value);
    setHighThreshold(newValue);

    // Ensure medium threshold is always less than high threshold
    if (mediumThreshold >= newValue) {
      setMediumThreshold(Math.max(0, newValue - 0.001));
    }
  };

  const handleMediumThresholdChange = (value) => {
    const newValue = parseFloat(value);

    // Ensure medium threshold doesn't exceed high threshold
    const maxValue = Math.max(0, highThreshold - 0.001);
    setMediumThreshold(Math.min(newValue, maxValue));
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setShowCancelModal(false);
        // Refresh user data to update subscription status
        await refreshUserData();
      } else {
        setError(data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      setError('Failed to cancel subscription. Please try again.');
      console.error('Cancel subscription error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteAccount = async () => {
     setIsLoading(true);
     setError('');
     setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/user/account', {
         method: 'DELETE',
         headers: {
		 'Authorization': `Bearer ${token}`,
		 'Content-Type': 'application/json',
	 },
      });

      const data = await response.json();
      if (response.ok) {
	      console.log('[Account Delete] Account deleted successfully, signing out...');
	      setShowDeleteModal(false);
	      localStorage.clear();
	      await logout();
	      window.location.href = '/';
      } else {
	      setError(data.error || 'Failed to delete account');
	      setIsLoading(false);
      }
    } catch (err) {
	    setError('Failed to delete account. Please try again.');
	    console.error('Delete account error:', err);
	    setIsLoading(false);
    }
  };
  // Load API keys and Obsidian preferences
  const loadIntegrationData = async () => {
    try {
      const token = localStorage.getItem('authToken');

      // Load API keys
      const keysResponse = await fetch('/api/settings/api-keys', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (keysResponse.ok) {
        const keysData = await keysResponse.json();
        setApiKeys(keysData.apiKeys || []);
      }

      // Load Obsidian preferences
      const prefsResponse = await fetch('/api/settings/obsidian-preferences', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (prefsResponse.ok) {
        const prefsData = await prefsResponse.json();
        setObsidianPreferences(prefsData);
      }
    } catch (error) {
      console.error('Error loading integration data:', error);
    }
  };

  // Load integration data when switching to integrations tab
  useEffect(() => {
    if (activeTab === 'integrations' && isAuthenticated) {
      loadIntegrationData();
    }
  }, [activeTab, isAuthenticated]);

  // Create new API key
  const handleCreateApiKey = async () => {
    setError(null);
    setIsLoading(true);

    // Auto-generate name if none provided
    let keyName = newApiKeyName.trim();
    if (!keyName) {
      const timestamp = new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      keyName = `API Key ${timestamp}`;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/settings/api-keys', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: keyName,
          scopes: ['read:transcripts']
        })
      });

      const data = await response.json();

      if (response.ok) {
        setGeneratedApiKey(data.apiKey);
        setNewApiKeyName('');
        await loadIntegrationData(); // Reload list
        setSuccess('API key created successfully');
      } else {
        const errorMsg = data.details ? `${data.error}: ${data.details}` : (data.error || 'Failed to create API key');
        console.error('[Settings] API key creation failed:', errorMsg, data);
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      setError(`Failed to create API key: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete API key
  const handleDeleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/settings/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('API key deleted successfully');
        await loadIntegrationData(); // Reload list
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete API key');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      setError('Failed to delete API key. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle API key visibility
  const handleToggleKeyVisibility = async (keyId) => {
    if (visibleKeys[keyId]) {
      // Hide the key
      setVisibleKeys(prev => ({ ...prev, [keyId]: false }));
    } else {
      // Show the key - need to fetch it if not already revealed
      if (!revealedKeys[keyId]) {
        setLoadingKeys(prev => ({ ...prev, [keyId]: true }));
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`/api/settings/api-keys/${keyId}/reveal`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            const data = await response.json();
            setRevealedKeys(prev => ({ ...prev, [keyId]: data.key }));
            setVisibleKeys(prev => ({ ...prev, [keyId]: true }));
          } else {
            const data = await response.json();
            setError(data.error || 'Failed to reveal API key');
          }
        } catch (error) {
          console.error('Error revealing API key:', error);
          setError('Failed to reveal API key. Please try again.');
        } finally {
          setLoadingKeys(prev => ({ ...prev, [keyId]: false }));
        }
      } else {
        setVisibleKeys(prev => ({ ...prev, [keyId]: true }));
      }
    }
  };

  // Copy API key to clipboard
  const handleCopyKey = async (keyId) => {
    const key = revealedKeys[keyId];
    if (!key) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(key);
        setSuccess('API key copied to clipboard!');
      } else {
        // Fallback for older browsers
        const input = document.createElement('input');
        input.value = key;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setSuccess('API key copied to clipboard!');
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy. Please select and copy manually.');
    }
  };

  // Update Obsidian preferences
  const handleUpdateObsidianPreferences = async (updates) => {
    const newPrefs = { ...obsidianPreferences, ...updates };
    setObsidianPreferences(newPrefs);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/settings/obsidian-preferences', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPrefs)
      });

      if (response.ok) {
        const result = await response.json();
        setObsidianSyncStatus({
          synced: true,
          lastModified: result.preferences?.lastModified || Date.now()
        });
      }
    } catch (error) {
      console.error('Error updating Obsidian preferences:', error);
      setObsidianSyncStatus({
        synced: false,
        error: 'Failed to sync preferences'
      });
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' },
    { id: 'integrations', label: 'Integrations', icon: '🔌' },
    { id: 'subscription', label: window.innerWidth <= 768 ? 'Billing' : 'Subscription & Billing', icon: '💳' },
    { id: 'data', label: window.innerWidth <= 768 ? 'Privacy' : 'Data & Privacy', icon: '🔒' }
  ];

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="settings-page">
      <Header />

      <main className="settings-main">
        <div className="settings-container">
          <div className="settings-header-section">
            <h1>Settings</h1>
            <p>Customize your Rec-Chord experience</p>
          </div>
          <div className="settings-sidebar">
            <h2>Settings</h2>
            <nav className="settings-nav">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                >
                  <span className="nav-icon">{tab.icon}</span>
                  <span className="nav-label">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="settings-content">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="settings-section">
                <h3>Profile Settings</h3>
                
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    disabled
                    className="form-input disabled"
                  />
                  <small>You sign in with Google. Email and password are managed through your Google account.</small>
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="settings-section">
                <h3>Application Preferences</h3>

                <div className="preference-group">
                  <h4>Appearance</h4>

                  <div className="theme-selector">
                    <label>Theme</label>
                    <div className="theme-options">
                      <button
                        className={`theme-option ${theme === 'light' ? 'selected' : ''}`}
                        onClick={() => setTheme('light')}
                        type="button"
                      >
                        <span className="theme-icon">☀️</span>
                        <span>Light</span>
                      </button>
                      <button
                        className={`theme-option ${theme === 'dark' ? 'selected' : ''}`}
                        onClick={() => setTheme('dark')}
                        type="button"
                      >
                        <span className="theme-icon">🌙</span>
                        <span>Dark</span>
                      </button>
                      <button
                        className={`theme-option ${theme === 'system' ? 'selected' : ''}`}
                        onClick={() => setTheme('system')}
                        type="button"
                      >
                        <span className="theme-icon">💻</span>
                        <span>System</span>
                      </button>
                    </div>
                    <small>Current: {resolvedTheme === 'dark' ? 'Dark' : 'Light'} mode</small>
                  </div>
                </div>

                <div className="preference-group">
                  <h4>Display Settings</h4>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={showConfidence}
                        onChange={(e) => setShowConfidence(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Show confidence percentages
                        <small>Display confidence percentage numbers next to each word</small>
                      </span>
                    </label>
                  </div>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={useCustomColors}
                        onChange={(e) => setUseCustomColors(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Use custom highlight colors
                        <small>Customize the background colors for each confidence level</small>
                      </span>
                    </label>
                  </div>

                  {useCustomColors && (
                    <div className="color-picker-group">
                      <h5>Word Background Highlight Colors</h5>
                      <small style={{ display: 'block', marginBottom: '12px', color: '#666' }}>
                        Customize the background colors for different confidence levels
                      </small>

                      <div className="color-picker-item">
                        <label>High Confidence</label>
                        <div className="color-picker-controls">
                          <input
                            type="color"
                            value={rgbaToHex(highConfidenceColor)}
                            onChange={(e) => setHighConfidenceColor(hexToRgba(e.target.value, 0.3))}
                            className="color-input"
                          />
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={getAlpha(highConfidenceColor)}
                            onChange={(e) => setHighConfidenceColor(setAlpha(highConfidenceColor, parseFloat(e.target.value)))}
                            className="opacity-slider"
                          />
                          <span className="opacity-value">{Math.round(getAlpha(highConfidenceColor) * 100)}%</span>
                          <button
                            type="button"
                            className="color-clear-btn"
                            onClick={() => setHighConfidenceColor(DEFAULT_COLORS.highConfidence)}
                            title="Reset to default"
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="color-clear-btn"
                            onClick={() => setHighConfidenceColor('rgba(0, 0, 0, 0)')}
                            title="No highlight"
                          >
                            None
                          </button>
                        </div>
                        <div className="color-preview" style={{ backgroundColor: highConfidenceColor }}>
                          Sample text
                        </div>
                        <small style={{ display: 'block', marginTop: '4px', color: '#888', fontSize: '0.85rem' }}>
                          Default: {rgbaToHex(DEFAULT_COLORS.highConfidence)} at {Math.round(getAlpha(DEFAULT_COLORS.highConfidence) * 100)}%
                        </small>
                      </div>

                      <div className="color-picker-item">
                        <label>Medium Confidence</label>
                        <div className="color-picker-controls">
                          <input
                            type="color"
                            value={rgbaToHex(mediumConfidenceColor)}
                            onChange={(e) => setMediumConfidenceColor(hexToRgba(e.target.value, 0.3))}
                            className="color-input"
                          />
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={getAlpha(mediumConfidenceColor)}
                            onChange={(e) => setMediumConfidenceColor(setAlpha(mediumConfidenceColor, parseFloat(e.target.value)))}
                            className="opacity-slider"
                          />
                          <span className="opacity-value">{Math.round(getAlpha(mediumConfidenceColor) * 100)}%</span>
                          <button
                            type="button"
                            className="color-clear-btn"
                            onClick={() => setMediumConfidenceColor(DEFAULT_COLORS.mediumConfidence)}
                            title="Reset to default"
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="color-clear-btn"
                            onClick={() => setMediumConfidenceColor('rgba(0, 0, 0, 0)')}
                            title="No highlight"
                          >
                            None
                          </button>
                        </div>
                        <div className="color-preview" style={{ backgroundColor: mediumConfidenceColor }}>
                          Sample text
                        </div>
                        <small style={{ display: 'block', marginTop: '4px', color: '#888', fontSize: '0.85rem' }}>
                          Default: {rgbaToHex(DEFAULT_COLORS.mediumConfidence)} at {Math.round(getAlpha(DEFAULT_COLORS.mediumConfidence) * 100)}%
                        </small>
                      </div>

                      <div className="color-picker-item">
                        <label>Low Confidence</label>
                        <div className="color-picker-controls">
                          <input
                            type="color"
                            value={rgbaToHex(lowConfidenceColor)}
                            onChange={(e) => setLowConfidenceColor(hexToRgba(e.target.value, 0.3))}
                            className="color-input"
                          />
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={getAlpha(lowConfidenceColor)}
                            onChange={(e) => setLowConfidenceColor(setAlpha(lowConfidenceColor, parseFloat(e.target.value)))}
                            className="opacity-slider"
                          />
                          <span className="opacity-value">{Math.round(getAlpha(lowConfidenceColor) * 100)}%</span>
                          <button
                            type="button"
                            className="color-clear-btn"
                            onClick={() => setLowConfidenceColor(DEFAULT_COLORS.lowConfidence)}
                            title="Reset to default"
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="color-clear-btn"
                            onClick={() => setLowConfidenceColor('rgba(0, 0, 0, 0)')}
                            title="No highlight"
                          >
                            None
                          </button>
                        </div>
                        <div className="color-preview" style={{ backgroundColor: lowConfidenceColor }}>
                          Sample text
                        </div>
                        <small style={{ display: 'block', marginTop: '4px', color: '#888', fontSize: '0.85rem' }}>
                          Default: {rgbaToHex(DEFAULT_COLORS.lowConfidence)} at {Math.round(getAlpha(DEFAULT_COLORS.lowConfidence) * 100)}%
                        </small>
                      </div>
                    </div>
                  )}

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={hideGhosted}
                        onChange={(e) => setHideGhosted(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Hide deleted words
                        <small>Hide words that have been deleted/ghosted from transcript</small>
                      </span>
                    </label>
                  </div>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={showSegments}
                        onChange={(e) => setShowSegments(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Show segments
                        <small>Display transcript grouped into segments</small>
                      </span>
                    </label>
                  </div>

                  {showSegments && (
                    <div className="slider-group" style={{ marginLeft: '32px' }}>
                      <label htmlFor="segment-duration">
                        Segment Duration: {segmentDuration} seconds
                        <small>Group words into segments of this duration</small>
                      </label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                        <input
                          id="segment-duration"
                          type="range"
                          min="5"
                          max="60"
                          step="5"
                          value={segmentDuration}
                          onChange={(e) => setSegmentDuration(parseInt(e.target.value))}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="number"
                          min="5"
                          max="60"
                          step="5"
                          value={segmentDuration}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (value >= 5 && value <= 60) {
                              setSegmentDuration(value);
                            }
                          }}
                          style={{ width: '60px' }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="preference-group">
                  <h4>Transcription Settings</h4>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={enableDiarization}
                        onChange={(e) => setEnableDiarization(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Enable speaker diarization
                        <small>Automatically identify and label different speakers in transcripts (requires inference server with diarization support)</small>
                      </span>
                    </label>
                  </div>
                </div>
                <div className="preference-group">
		    <h4>Upload & File Management</h4>
		    <div className="checkbox-group">
		      <label className="checkbox-label">
		       <input
		    	type="checkbox"
		    	checked={enableFolderUpload}
		    	onChange={(e) => setEnableFolderUpload(e.target.checked)}
		       />
		       <span className="checkbox-text">
		          Enable folder upload
		        <small>Show the "Upload Folder" option in the main interface</small>
		       </span>
		      </label>
		    </div>
		 </div>
                {/* Live Transcription Settings - Premium Only */}
                {currentTier === 'premium' && (
                  <div className="preference-group">
                    <h4>Live Transcription Settings</h4>

                    <div className="slider-group">
                      <label htmlFor="max-silence-interval">
                        Max Silence Interval: {maxSilenceInterval.toFixed(1)} seconds
                        <small>Maximum duration of silence before splitting into segments during live transcription</small>
                      </label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                        <input
                          id="max-silence-interval"
                          type="range"
                          min="0.1"
                          max="5.0"
                          step="0.1"
                          value={maxSilenceInterval}
                          onChange={(e) => setMaxSilenceInterval(parseFloat(e.target.value))}
                          style={{ flex: 1 }}
                        />
                        <input
                          type="number"
                          min="0.1"
                          max="5.0"
                          step="0.1"
                          value={maxSilenceInterval}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (value >= 0.1 && value <= 5.0) {
                              setMaxSilenceInterval(value);
                            }
                          }}
                          style={{ width: '60px' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="preference-group">
                  <h4>Editing Mode Defaults</h4>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={showTypingModeToggle}
                        onChange={(e) => setShowTypingModeToggle(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Show Typing Mode Toggle
                        <small>Display the typing mode toggle button in the transcript controls</small>
                      </span>
                    </label>
                  </div>

                  {showTypingModeToggle && (
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={typingMode}
                          onChange={(e) => setTypingMode(e.target.checked)}
                        />
                        <span className="checkbox-text">
                          Start in Typing Mode
                          <small>Enable typing mode by default when opening transcripts</small>
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={showClickToDeleteToggle}
                        onChange={(e) => setShowClickToDeleteToggle(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Show Click-to-Delete Mode Toggle
                        <small>Display the click-to-delete mode toggle button in the transcript controls</small>
                      </span>
                    </label>
                  </div>

                  {showClickToDeleteToggle && (
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={clickToDeleteMode}
                          onChange={(e) => setClickToDeleteMode(e.target.checked)}
                        />
                        <span className="checkbox-text">
                          Start in Click-to-Delete Mode
                          <small>Enable click-to-delete mode by default when opening transcripts</small>
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={showDeleteModeToggle}
                        onChange={(e) => setShowDeleteModeToggle(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Show Delete Mode Toggle
                        <small>Display the delete mode toggle button in the transcript controls</small>
                      </span>
                    </label>
                  </div>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={showSuggestionsModeToggle}
                        onChange={(e) => setShowSuggestionsModeToggle(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Show Suggestions Mode Toggle
                        <small>Display the suggestions mode toggle button in the transcript controls</small>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="preference-group">
                  <h4>Navigation Settings</h4>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={showConfidenceLegend}
                        onChange={(e) => setShowConfidenceLegend(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Show Confidence Counter
                        <small>Display H/M/L confidence word counts for bin navigation</small>
                      </span>
                    </label>
                  </div>

                  {showConfidenceLegend && (
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={enableBinNavigation}
                          onChange={(e) => setEnableBinNavigation(e.target.checked)}
                        />
                        <span className="checkbox-text">
                          Enable Bin Navigation
                          <small>Arrow keys navigate within confidence bins instead of word-by-word</small>
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="preference-group">
                  <h4>AI Features</h4>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={useAiGeneratedNames}
                        onChange={(e) => setUseAiGeneratedNames(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Use AI-generated names
                        <small>Automatically generate descriptive names for new transcripts using AI</small>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="preference-group">
                  <h4>Playback Settings</h4>

                  <div className="slider-group">
                    <label>Skip Forward/Backward Duration</label>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      step="1"
                      value={skipDuration}
                      onChange={(e) => setSkipDuration(parseInt(e.target.value))}
                      className="slider"
                    />
                    <span className="slider-value">{skipDuration} seconds</span>
                    <small>How many seconds to skip when using skip buttons</small>
                  </div>
                </div>

                <div className="preference-group">
                  <h4>Sharing Settings</h4>

                  <div className="slider-group">
                    <label>Share Link Expiration</label>
                    <input
                      type="range"
                      min="1"
                      max="90"
                      step="1"
                      value={shareLinkExpirationDays}
                      onChange={(e) => setShareLinkExpirationDays(parseInt(e.target.value))}
                      className="slider"
                    />
                    <span className="slider-value">{shareLinkExpirationDays} days</span>
                    <small>How long shared transcript links remain valid</small>
                  </div>
                </div>

                <div className="preference-group">
                  <h4>Safety Settings</h4>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={confirmTranscriptDelete}
                        onChange={(e) => setConfirmTranscriptDelete(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Confirm before deleting transcripts
                        <small>Show a confirmation dialog when deleting transcripts from the Dashboard</small>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="preference-group">
                  <h4>Confidence Thresholds</h4>

                  <div className="slider-group">
                    <label>High Confidence (Green)</label>
                    <input
                      type="range"
                      min="0.5"
                      max="1"
                      step="0.001"
                      value={highThreshold}
                      onChange={(e) => handleHighThresholdChange(e.target.value)}
                      className="slider"
                    />
                    <span className="slider-value">{(highThreshold * 100).toFixed(1)}%</span>
                  </div>

                  <div className="slider-group">
                    <label>Medium Confidence (Yellow)</label>
                    <input
                      type="range"
                      min="0"
                      max={highThreshold - 0.001}
                      step="0.001"
                      value={mediumThreshold}
                      onChange={(e) => handleMediumThresholdChange(e.target.value)}
                      className="slider"
                    />
                    <span className="slider-value">{(mediumThreshold * 100).toFixed(1)}%</span>
                  </div>
                </div>
                
                <div className="preference-group">
                  <h4>Notifications</h4>
                  
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={notifications}
                        onChange={(e) => setNotifications(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        Browser notifications
                        <small>Get notified when transcription is complete</small>
                      </span>
                    </label>
                  </div>
                </div>


              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="settings-section">
                <h3>Subscription & Billing</h3>

                <QuotaDisplay showUpgrade={true} />

                <div className="current-plan-card">
                  <div className="plan-header">
                    <h4>Current Plan</h4>
                    <span className={`plan-badge ${currentTier}`}>
                      {limits.name} Plan
                    </span>
                  </div>

                  <div className="plan-details">
                    <div className="plan-feature">
                      <span className="feature-label">Monthly Transcriptions:</span>
                      <span className="feature-value">
                        {limits.transcriptions === Infinity ? 'Unlimited' : limits.transcriptions}
                      </span>
                    </div>

                    <div className="plan-feature">
                      <span className="feature-label">Payment Status:</span>
                      <span className={`status ${user.paymentStatus}`}>
                        {user.paymentStatus === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {user.subscription && (
                      <div className="plan-feature">
                        <span className="feature-label">Billing Period:</span>
                        <span className="feature-value">
                          {new Date(user.subscription.currentPeriodStart).toLocaleDateString()} - {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {process.env.NODE_ENV === 'development' && (
                  <div className="debug-section" style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                    <h4>Debug Information</h4>
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', marginBottom: '10px' }}>
                      <div><strong>User Tier:</strong> {user.tier || 'undefined'}</div>
                      <div><strong>Payment Status:</strong> {user.paymentStatus || 'undefined'}</div>
                      <div><strong>Current Tier (hook):</strong> {currentTier}</div>
                      <div><strong>Has Subscription:</strong> {user.subscription ? 'Yes' : 'No'}</div>
                      {user.subscription && (
                        <div><strong>Subscription Tier:</strong> {user.subscription.tier || 'undefined'}</div>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        setIsLoading(true);
                        try {
                          await refreshUserData();
                          setSuccess('User data refreshed successfully');
                        } catch (err) {
                          setError('Failed to refresh user data: ' + err.message);
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      className="form-button secondary"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Refreshing...' : 'Refresh User Data'}
                    </button>
                  </div>
                )}

                <div className="plan-actions">
                  <h4>Plan Management</h4>
                  <div className="action-buttons">
                    {canUpgrade && (
                      <Link to="/plans-and-payment" className="form-button primary">
                        Upgrade to {getNextTierName()}
                      </Link>
                    )}

                    {currentTier !== 'free' && (
                      <Link to="/plans-and-payment" className="form-button secondary">
                        Change Plan
                      </Link>
                    )}

                    <Link to="/plans-and-payment" className="form-button secondary">
                      Update Payment Method
                    </Link>
                  </div>
                </div>

                <div className="billing-actions">
                  <h4>Billing</h4>
                  <div className="action-buttons">
                    <Link to="/plans-and-payment" className="form-button secondary">
                      View Billing History
                    </Link>

                    {currentTier !== 'free' && user.subscription?.stripeSubscriptionId && (
                      <button
                        className="form-button danger"
                        onClick={() => setShowCancelModal(true)}
                        disabled={isLoading}
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div className="settings-section">
                <h3>Integrations</h3>
                <p>Connect Rec-Chord with external tools and services</p>

                {process.env.REACT_APP_ENABLE_API_KEYS !== 'false' ? (
                  <>
                    {/* API Keys Section */}
                    <div className="preference-group">
                  <h4>API Keys</h4>
                  <p>Generate API keys to connect Rec-Chord with third-party applications like Obsidian.</p>

                  {/* Create new API key */}
                  <div style={{ marginTop: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={newApiKeyName}
                        onChange={(e) => setNewApiKeyName(e.target.value)}
                        placeholder="API Key Name (e.g., 'Obsidian Plugin')"
                        className="form-input"
                        style={{ flex: 1 }}
                      />
                      <button
                        onClick={handleCreateApiKey}
                        disabled={isLoading}
                        className="form-button primary"
                      >
                        Generate API Key
                      </button>
                    </div>
                    <small>Create a new API key for integrations. Name is optional - one will be generated if not provided. Keep your key secure - it won't be shown again!</small>
                  </div>

                  {/* Show generated API key */}
                  {generatedApiKey && (
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '8px',
                      marginBottom: '1rem'
                    }}>
                      <h5 style={{ marginTop: 0, color: '#856404' }}>⚠️ Save Your API Key</h5>
                      <p style={{ marginBottom: '0.5rem', color: '#856404' }}>
                        Copy this key now - you won't be able to see it again!
                      </p>
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      }}>
                        <input
                          type="text"
                          value={generatedApiKey}
                          readOnly
                          style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            color: '#333',
                            backgroundColor: 'transparent',
                            padding: 0
                          }}
                          onClick={(e) => e.target.select()}
                        />
                        <button
                          onClick={async () => {
                            try {
                              if (navigator.clipboard && navigator.clipboard.writeText) {
                                await navigator.clipboard.writeText(generatedApiKey);
                                setSuccess('API key copied to clipboard!');
                              } else {
                                // Fallback for older browsers
                                const input = document.createElement('input');
                                input.value = generatedApiKey;
                                document.body.appendChild(input);
                                input.select();
                                document.execCommand('copy');
                                document.body.removeChild(input);
                                setSuccess('API key copied to clipboard!');
                              }
                            } catch (err) {
                              console.error('Failed to copy:', err);
                              setError('Failed to copy. Please select and copy manually.');
                            }
                          }}
                          className="form-button secondary"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Copy
                        </button>
                      </div>
                      <button
                        onClick={() => setGeneratedApiKey(null)}
                        className="form-button secondary"
                        style={{ marginTop: '0.5rem' }}
                      >
                        I've saved it
                      </button>
                    </div>
                  )}

                  {/* List existing API keys */}
                  {apiKeys.length > 0 ? (
                    <div style={{ marginTop: '1rem' }}>
                      <h5>Your API Keys</h5>
                      {apiKeys.map(key => (
                        <div key={key.id} style={{
                          padding: '1rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          marginBottom: '0.75rem'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                {key.name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#999' }}>
                                Created: {new Date(key.createdAt).toLocaleDateString()}
                                {key.lastUsedAt && ` • Last used: ${new Date(key.lastUsedAt).toLocaleDateString()}`}
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteApiKey(key.id)}
                              className="form-button danger"
                              disabled={isLoading}
                              style={{ marginLeft: '1rem' }}
                            >
                              Delete
                            </button>
                          </div>

                          {/* API Key Display with Show/Hide and Copy */}
                          <div style={{
                            display: 'flex',
                            gap: '0.5rem',
                            alignItems: 'center',
                            backgroundColor: '#f5f5f5',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            flexWrap: 'nowrap'
                          }}>
                            <div style={{
                              flex: 1,
                              minWidth: 0,
                              fontFamily: 'monospace',
                              fontSize: '0.9rem',
                              color: '#333',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {visibleKeys[key.id] && revealedKeys[key.id]
                                ? revealedKeys[key.id]
                                : `${key.prefix}••••••••••••••••••••`
                              }
                            </div>
                            <button
                              onClick={() => handleToggleKeyVisibility(key.id)}
                              disabled={loadingKeys[key.id]}
                              className="form-button secondary"
                              style={{ whiteSpace: 'nowrap', minWidth: '40px', flexShrink: 0 }}
                              title={visibleKeys[key.id] ? 'Hide API key' : 'Show API key'}
                            >
                              {loadingKeys[key.id] ? '...' : (visibleKeys[key.id] ? '👁️' : '👁️‍🗨️')}
                            </button>
                            {visibleKeys[key.id] && revealedKeys[key.id] && (
                              <button
                                onClick={() => handleCopyKey(key.id)}
                                className="form-button secondary"
                                style={{ whiteSpace: 'nowrap', minWidth: '40px', flexShrink: 0 }}
                                title="Copy API key to clipboard"
                              >
                                📋
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '8px', textAlign: 'center', color: '#666' }}>
                      No API keys yet. Create one to get started!
                    </div>
                  )}
                </div>

                    {/* Obsidian Plugin Settings */}
                    <div className="preference-group" style={{ marginTop: '2rem' }}>
                      <h4>Obsidian Plugin Settings</h4>
                      <p>Configure how the Rec-Chord Obsidian plugin syncs your transcripts.</p>

                      <div style={{ marginTop: '1.5rem' }}>
                        <label htmlFor="recordingsFolder" className="form-label">
                          Recordings Folder
                        </label>
                        <input
                          id="recordingsFolder"
                          type="text"
                          value={obsidianPreferences.recordingsFolder || ''}
                          onChange={(e) => handleUpdateObsidianPreferences({ recordingsFolder: e.target.value })}
                          className="form-input"
                          placeholder="Rec-Chord"
                        />
                        <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                          The folder where audio recordings will be saved in your Obsidian vault
                        </small>
                      </div>

                      <div style={{ marginTop: '1rem' }}>
                        <label htmlFor="transcriptsFolder" className="form-label">
                          Transcripts Folder
                        </label>
                        <input
                          id="transcriptsFolder"
                          type="text"
                          value={obsidianPreferences.transcriptsFolder || ''}
                          onChange={(e) => handleUpdateObsidianPreferences({ transcriptsFolder: e.target.value })}
                          className="form-input"
                          placeholder="Rec-Chord"
                        />
                        <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                          The folder where transcripts will be saved in your Obsidian vault
                        </small>
                      </div>

                      <div style={{ marginTop: '1rem' }}>
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            checked={obsidianPreferences.syncAudio || false}
                            onChange={(e) => handleUpdateObsidianPreferences({ syncAudio: e.target.checked })}
                          />
                          <span>Sync audio files</span>
                        </label>
                        <small style={{ display: 'block', marginTop: '0.25rem', marginLeft: '1.5rem', color: '#666' }}>
                          Download and save audio files along with transcripts
                        </small>
                      </div>

                      {/* Sync Status */}
                      {obsidianSyncStatus && (
                        <div style={{
                          marginTop: '1.5rem',
                          padding: '1rem',
                          backgroundColor: obsidianSyncStatus.synced ? '#f0fdf4' : '#fef2f2',
                          border: `1px solid ${obsidianSyncStatus.synced ? '#86efac' : '#fecaca'}`,
                          borderRadius: '8px'
                        }}>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: obsidianSyncStatus.synced ? '#166534' : '#991b1b' }}>
                            {obsidianSyncStatus.synced ? (
                              <>✓ Settings synced {obsidianSyncStatus.lastModified ? `at ${new Date(obsidianSyncStatus.lastModified).toLocaleString()}` : 'successfully'}</>
                            ) : (
                              <>⚠ {obsidianSyncStatus.error || 'Settings not synced'}</>
                            )}
                          </p>
                        </div>
                      )}

                      <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
                          💡 <strong>Tip:</strong> Changes to these settings will be automatically synced with your Obsidian plugin.
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="preference-group">
                    <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                      API integrations are currently disabled.
                    </p>
                  </div>
                )}

              </div>
            )}

            {activeTab === 'data' && (
              <div className="settings-section">
                <h3>Data & Privacy</h3>

                <div className="data-section">
                  <h4>Export Your Data</h4>
                  <p>Download all your transcripts and account data as a ZIP file.</p>
                  <Link to="/dashboard" className="form-button secondary">
                    Go to Dashboard to Export Data
                  </Link>
                  <small style={{display: 'block', marginTop: '0.5rem', color: '#666'}}>
                    You can export individual transcripts or all your data from the Dashboard page.
                  </small>
                </div>

                <div className="data-section">
                  <h4>Data Storage</h4>
                  <p>Your transcripts are securely stored and accessible through your Dashboard according to our privacy policy.</p>
                </div>

                <div className="data-section danger-zone">
                  <h4>Delete Account</h4>
                  <p>
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <button
                    className="form-button danger"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={isLoading}
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => !isLoading && setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Subscription</h3>
            <p>
              Are you sure you want to cancel your subscription? Your plan will remain active until the end of the current billing period, after which you'll be downgraded to the free plan.
            </p>
            <div className="modal-actions">
              <button
                className="form-button secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={isLoading}
              >
                Keep Subscription
              </button>
              <button
                className="form-button danger"
                onClick={handleCancelSubscription}
                disabled={isLoading}
              >
                {isLoading ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !isLoading && setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Account</h3>
            <p>
              <strong>Warning:</strong> This action cannot be undone. Deleting your account will:
            </p>
            <ul style={{ textAlign: 'left', marginLeft: '20px' }}>
              <li>Cancel your subscription immediately</li>
              <li>Delete all your transcripts permanently</li>
              <li>Remove all your account data</li>
            </ul>
            <p>Are you absolutely sure you want to delete your account?</p>
            <div className="modal-actions">
              <button
                className="form-button secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="form-button danger"
                onClick={handleDeleteAccount}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
